import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from '../../../../../common/services/api'
import { buildEncodedForm } from '../../../../../common/utils'
import { showToast } from '../../../../../common/utils'
import { UploadFile } from 'antd'
import dayjs from 'dayjs'
import {
  COMMUNICATION_RECORDS_URL,
  COMMUNICATION_RECORDS_QUERY_KEY
} from './useCommunicationRecordCreate'
import { CreateCommunicationRecordBody } from './useCommunicationRecordCreate'

type UseCommunicationRecordUpdateProps = {
  recordId?: string
  onSuccess?: () => void
}

type UseCommunicationRecordUpdateReturn = {
  updateCommunicationRecord: (
    values: CreateCommunicationRecordBody,
    fileList?: UploadFile[]
  ) => Promise<unknown>
  isUpdateLoading: boolean
}

export const useCommunicationRecordUpdate = ({
  recordId,
  onSuccess
}: UseCommunicationRecordUpdateProps): UseCommunicationRecordUpdateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: (err?.message || err?.msg || 'Something went wrong') as string,
      type: 'error'
    })
  }

  const handleSuccess = () => {
    showToast({
      title: 'Updated Document',
      type: 'success'
    })
  }

  const updateFunction = async (
    values: CreateCommunicationRecordBody,
    fileList?: UploadFile[]
  ) => {
    if (!recordId) {
      throw new Error('Record ID is required for update')
    }

    const hasFile = fileList && fileList.length > 0 && fileList[0].originFileObj

    const formattedValues = {
      ...values,
      ...(values.dateSent && {
        dateSent: dayjs.isDayjs(values.dateSent)
          ? values.dateSent.toISOString()
          : dayjs(values.dateSent).toISOString()
      })
    }

    if (hasFile) {
      const formData = buildEncodedForm({
        ...formattedValues,
        pdf: { file: fileList[0].originFileObj }
      })
      return postJSON({
        url: `${COMMUNICATION_RECORDS_URL}/${recordId}`,
        method: 'PUT',
        body: formData,
        contentType: 'multipart/form-data'
      })
    } else {
      return postJSON({
        url: `${COMMUNICATION_RECORDS_URL}/${recordId}`,
        method: 'PUT',
        json: formattedValues
      })
    }
  }

  const { mutateAsync: updateMutation, isLoading: isUpdateLoading } =
    useMutation<
      unknown,
      unknown,
      { values: CreateCommunicationRecordBody; fileList?: UploadFile[] },
      unknown
    >(
      [COMMUNICATION_RECORDS_QUERY_KEY, 'update', recordId],
      ({ values, fileList }) => updateFunction(values, fileList),
      {
        onSuccess: () => {
          handleSuccess()
          queryClient.invalidateQueries([COMMUNICATION_RECORDS_QUERY_KEY])
          onSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const updateCommunicationRecord = async (
    values: CreateCommunicationRecordBody,
    fileList?: UploadFile[]
  ) => {
    try {
      const result = await updateMutation({ values, fileList })
      return result
    } catch (err) {
      return err
    }
  }

  return {
    updateCommunicationRecord,
    isUpdateLoading
  }
}
